// src/contracts/estimates/types.ts
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { List } from "reactstrap";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export type EstimatePurpose = {
  cbEmergency: boolean;
  cbReview: boolean;
  cbMemo: boolean;
  cbReply: boolean;
  cbReuse: boolean;
};

export type CoverForm = {
  coverSheetName?: string;
  companyName: string;
  companyTel: string;
  recipientName: string;
  recipientFax: string;
  recipientHP: string;
  siteName: string;
  sender: string;
  senderPosition?: string;
  senderMP: string;
  purpose: EstimatePurpose;
  message?: string;
  estimateDate: string;
};

export const defaultCoverForm: CoverForm = {
  coverSheetName: "표지",
  companyName: "",
  companyTel: "",
  recipientName: "",
  recipientFax: "",
  recipientHP: "",
  siteName: "",
  sender: "",
  senderPosition: "",
  senderMP: "",
  purpose: {
    cbEmergency: false,
    cbReview: false,
    cbMemo: false,
    cbReply: false,
    cbReuse: false,
  },
  message: "",
  estimateDate: new Date().toISOString().slice(0, 10),
};

export type ContentForm = {
  contentType: string;
  deliveryDate: number;
  contentTitle: string;
  deliveryLocation: string;
  personInCharge: string;
  items: {
    rowType: string;
    item: string;
    size: string;
    unit: string;
    qty: number;
    unitPrice: number;
    amount: number;
    remark: string;
    description: string | null;
  }[];
  remark: string[];
}

export const COMPANY_IN_CHARGE_TYPE: string[] = [
  "진영",
  // "마르스"
  "문하우징"
]

export const COMPARISION_COMPANY_IN_CHARGE_TYPE: string[] = [
  "세움모빌",
  "삼진넥스틸"
]

export const SALE_TYPE: string[] = [
  "임대",
  "판매",
  "보수"
]

export const ROW_TYPE: string[] = [
  "아이템",
  "설명",
  "소계"
]

export const defaultDeliveryDates: number[] = [3, 10]

export const defaultContentForm: ContentForm = {
  contentType: SALE_TYPE[0],
  deliveryDate: 3,
  contentTitle: "",
  deliveryLocation: "",
  personInCharge: "",
  items: [{
    rowType: ROW_TYPE[0],
    item: "",
    size: "",
    unit: "",
    qty: 0,
    unitPrice: 0,
    amount: 0,
    remark: "",
    description: null
  }],
  remark: []
}

export interface EstimateDto {
  id: string;
  companyInCharge: string;
  cover: CoverForm;
  content: ContentForm;
  createdTimestamp: string;
  modifiedTimestamp: string;
}

export const defaultEstimateDto: EstimateDto = {
  id: "",
  companyInCharge: "진영",
  cover: defaultCoverForm,
  content: defaultContentForm,
  createdTimestamp: dayjs().tz('Asia/Seoul')
                  .format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
  modifiedTimestamp: ""
}

export type PostMsg =
  | { type: "POPUP_READY" }
  | { type: "ESTIMATE_DATA"; payload: EstimateDto };

const estimateResolverSchema: Yup.ObjectSchema<EstimateDto> = Yup.object({
    id: Yup.string().uuid().required(),
    companyInCharge: Yup.string().default("진영"),
    cover: Yup.object({
        companyName: Yup.string().required(),
        companyTel: Yup.string().required(),
        recipientName: Yup.string().required(),
        recipientFax: Yup.string().required(),
        recipientHP: Yup.string().required(),
        siteName: Yup.string().required(),
        sender: Yup.string().required(),
        senderPosition: Yup.string().required(),
        senderMP: Yup.string().required(),
        purpose: Yup.object({
            cbEmergency: Yup.boolean().required(),
            cbReview: Yup.boolean().required(),
            cbMemo: Yup.boolean().required(),
            cbReply: Yup.boolean().required(),
            cbReuse: Yup.boolean().required(),
        }).required(),
        message: Yup.string().optional(),
        estimateDate: Yup.string().required(),
    }),
    content: Yup.object({
        contentType: Yup.string().oneOf(SALE_TYPE).default(SALE_TYPE[0]).required(),
        deliveryDate: Yup.number().default(defaultDeliveryDates[0]).required(),
        contentTitle: Yup.string().required(),
        deliveryLocation: Yup.string().required(),
        personInCharge: Yup.string().required(),
        items: Yup.array().of(
            Yup.object({
                rowType: Yup.string().default("").required(),
                item: Yup.string().default("").required(),
                size: Yup.string().default("").required(),
                unit: Yup.string().default(""),
                qty: Yup.number().default(0).required(),
                unitPrice: Yup.number().required(),
                amount: Yup.number().required(),
                remark: Yup.string().default("").required(),
                description: Yup.string().nullable().default(null).required()
            })
        )
        .min(1, "품목을 1개 이상 입력하세요")
        .required(),
        remark: Yup.array().of(Yup.string().default("")).required(),
    }),
    createdTimestamp: Yup.string().default("").required(),
    modifiedTimestamp: Yup.string().default("").required(),
    }).required()

export const estimateResolver = yupResolver(estimateResolverSchema);


export const coverFields = [
  { label: "일  자", name: "estimateDate", type: "date", required: true, multiline: false },
  { label: "업체명", name: "companyName", type: "text", required: true, multiline: false },
  { label: "전화번호(TEL)", name: "companyTel", type: "text", required: true, multiline: false },
  { label: "수신자 이름", name: "recipientName", type: "text", required: true, multiline: false },
  { label: "수신자 FAX", name: "recipientFax", type: "text", required: true, multiline: false },
  { label: "수신자 (HP)", name: "recipientHP", type: "text", required: true, multiline: false },
  { label: "현장명", name: "siteName", type: "text", required: true, multiline: false },
  { label: "발신자 직책", name: "senderPosition", type: "text", required: false, multiline: false},
  { label: "발신자", name: "sender", type: "text", required: true, multiline: false },
  { label: "발신자 (MP)", name: "senderMP", type: "text", required: true, multiline: false },
  { label: "MESSAGE", name: "message", type: "text", required: false, multiline: true },
] as const;

export const contentFields = [
  { label: "판매구분", name: "contentType", type: "text" },
  { label: "납기일", name: "deliveryDate", type: "number" },
  { label: "제목", name: "contentTitle", type: "text" },
  { label: "납품 장소", name: "deliveryLocation", type: "text" },
  { label: "담당자", name: "personInCharge", type: "text" },
]

export const contentItemFields = [
  { label: "품명", name: "item", type: "string", required: true },
  { label: "규격", name: "size", type: "string", required: true },
  { label: "단위", name: "unit", type: "string", required: true },
  { label: "수량", name: "qty", type: "string", required: true },
  { label: "단가", name: "unitPrice", type: "string", required: true },
  { label: "금액", name: "amount", type: "string", required: true },
  { label: "비고", name: "remark", type: "string", required: false },
] as const;

export const UNIT_CHOICES = [" ", "개월", "동"];

export const REMARK_JINYOUNG = [
  "현장도착회수도(상하차장비:발주처 시행) 5톤 이상 지게차 필요",
  "외부전기인입연결, 급수연결, 내부청소관리 및 소모품(세정액 등) 보충, 오수 배출 등 - 발주처 시행",																
  "전기는 220V 단상 , 수도는 엑셀 파이프 15A로 상수도 연결 혹은 내부 보조물탱크(60L)에 채워서 사용",															
  "결제조건: 출고 전 3개월치 임대료 및 왕복 운반비 선결제 / 4개월 임대료부터는 월 별 결제",																
  "결제계좌: 기업은행 450-063460-04-018 진영하우징㈜"
];

export const REMARK_MARS = [
  "현장도착회수도(상하차장비-마르스 시행) / 7톤이상 지게차",
  "외부전기인입연결,직수연결, 오수배출,내부청소관리, 소모품 보충 - 발주처 시행",																		
  "결제조건 : 출고 전 2개월분 임대료, 왕복운반비 선결제 조건 / 3개월 이후 발생 임대료 월별 선납 조건",																		
  "결제계좌: 기업 547-060044-01-017 마르스㈜"																		
];

export const REMARK_MUN = [
  "현장도착회수도(상하차장비-마르스 시행) / 7톤이상 지게차",
  "외부전기인입연결,직수연결, 오수배출,내부청소관리, 소모품 보충 - 발주처 시행",																		
  "결제조건 : 출고 전 2개월분 임대료, 왕복운반비 선결제 조건 / 3개월 이후 발생 임대료 월별 선납 조건",																		
  "결제계좌: 기업 547-060044-01-017 문하우징 주식회사"																		
];


export function fromJson(json: string): EstimateDto {
  return JSON.parse(json) as EstimateDto;
}

export function fromJsonArray(json: string): EstimateDto[] {
  return JSON.parse(json) as EstimateDto[];
}

export function toJson(dto: EstimateDto): string {
  return JSON.stringify(dto);
}

export function createEmptyEstimate(): EstimateDto {
  return {
    ...defaultEstimateDto,
    id: crypto.randomUUID(),
    createdTimestamp: dayjs().tz('Asia/Seoul').format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
  };
}

